/**
 * PropertyDetailsTab — physical facts.
 *
 * ReSimpli L1 land support: the exposed fields swap based on
 * `property.deal_type`. Houses see beds/baths/sqft/year built.
 * Land sees acreage / zoning / road access / utilities / topography.
 * Both keep `lot_size_sqft` + `property_type` (universal). Adjustment
 * lives in one place so future asset types (multifamily, mobile home
 * park, storage) can plug in as another FIELDS array.
 */
import React, { useCallback, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Trees, Pencil, Save, X, Loader2 } from "lucide-react";

const DEAL_TYPES = [
  { key: "house",       label: "House",        icon: Building2 },
  { key: "land",        label: "Land",         icon: Trees },
  { key: "multifamily", label: "Multifamily",  icon: Building2 },
];

const HOUSE_FIELDS = [
  { key: "bedrooms",       label: "Bedrooms",        type: "number", step: 1 },
  { key: "bathrooms",      label: "Bathrooms",       type: "number", step: 0.5 },
  { key: "sqft",           label: "Square Feet",     type: "number", step: 1 },
  { key: "year_built",     label: "Year Built",      type: "number", step: 1 },
  { key: "lot_size_sqft",  label: "Lot Size (sqft)", type: "number", step: 1 },
  { key: "property_type",  label: "Property Type",   type: "text" },
];

const LAND_FIELDS = [
  { key: "acreage",             label: "Acreage",          type: "number", step: 0.01 },
  { key: "zoning",              label: "Zoning",           type: "text",
    placeholder: "R-1, AG, MU…" },
  { key: "road_access",         label: "Road Access",      type: "select",
    options: ["", "paved", "gravel", "dirt", "none"] },
  { key: "utilities_available", label: "Utilities",        type: "text",
    placeholder: "water, power, septic…" },
  { key: "topography",          label: "Topography",       type: "select",
    options: ["", "flat", "rolling", "wooded", "wetland", "mixed"] },
  { key: "lot_size_sqft",       label: "Lot Size (sqft)",  type: "number", step: 1 },
];

export default function PropertyDetailsTab({ property, onPropertyChange }) {
  const dealType = property.deal_type || "house";
  const fields = useMemo(
    () => (dealType === "land" ? LAND_FIELDS : HOUSE_FIELDS),
    [dealType],
  );

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => {
    const base = Object.fromEntries(fields.map(({ key }) => [key, property[key] ?? ""]));
    base.deal_type = dealType;
    return base;
  });
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    const base = Object.fromEntries(fields.map(({ key }) => [key, property[key] ?? ""]));
    base.deal_type = dealType;
    setForm(base);
  }, [property, fields, dealType]);

  // If the deal_type changes mid-edit, swap the field set live.
  const activeFields = useMemo(
    () => (form.deal_type === "land" ? LAND_FIELDS : HOUSE_FIELDS),
    [form.deal_type],
  );

  const save = async () => {
    setSaving(true);
    try {
      const patch = { deal_type: form.deal_type };
      for (const { key, type } of activeFields) {
        const raw = form[key];
        if (raw === "" || raw == null) { patch[key] = null; continue; }
        patch[key] = type === "number" ? Number(raw) : raw;
      }
      const { data } = await api.patch(`/v3/properties/${property.id}`, patch);
      toast.success("Property details saved");
      onPropertyChange?.(data);
      setEditing(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const DealIcon = dealType === "land" ? Trees : Building2;

  return (
    <div className="space-y-4" data-testid="tab-panel-property-details">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <DealIcon className="w-3.5 h-3.5" />
            {dealType === "land" ? "Land Details" : "Physical Details"}
            <span className="ml-2 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[9px] px-1.5 py-0.5 tracking-widest">
              {DEAL_TYPES.find((d) => d.key === dealType)?.label || "House"}
            </span>
          </div>
          {!editing ? (
            <button
              type="button" onClick={() => setEditing(true)}
              data-testid="property-details-edit"
              className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                type="button" onClick={() => { setEditing(false); reset(); }}
                data-testid="property-details-cancel"
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Cancel
              </button>
              <button
                type="button" onClick={save} disabled={saving}
                data-testid="property-details-save"
                className="text-[11px] font-semibold rounded-md bg-slate-900 text-white px-2.5 py-1 hover:bg-slate-800 inline-flex items-center gap-1"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
          )}
        </div>

        {/* Deal-type selector (edit mode only). Swaps the field set live. */}
        {editing && (
          <div className="px-4 pt-3 border-b border-slate-100 bg-amber-50/40">
            <div className="text-[9.5px] uppercase tracking-widest font-black text-amber-700 mb-1.5">
              Deal Type
            </div>
            <div className="flex flex-wrap gap-1.5 pb-3">
              {DEAL_TYPES.map((d) => {
                const active = form.deal_type === d.key;
                return (
                  <button
                    key={d.key} type="button"
                    onClick={() => setForm((f) => ({ ...f, deal_type: d.key }))}
                    data-testid={`property-details-deal-type-${d.key}`}
                    className={
                      "inline-flex items-center gap-1.5 rounded-full border text-[11.5px] font-semibold px-3 py-1 transition-colors " +
                      (active
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-white text-slate-700 border-slate-200 hover:border-amber-300")
                    }
                  >
                    <d.icon className="w-3 h-3" /> {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          {activeFields.map((f) => (
            <div key={f.key}>
              <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1">
                {f.label}
              </div>
              {editing ? (
                f.type === "select" ? (
                  <select
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`property-details-${f.key}-input`}
                    className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white"
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o || "—"}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.type} step={f.step ?? undefined}
                    placeholder={f.placeholder}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`property-details-${f.key}-input`}
                    className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                )
              ) : (
                <div
                  data-testid={`property-details-${f.key}`}
                  className="text-[14px] font-semibold text-slate-900 tabular-nums"
                >
                  {property[f.key] ?? "—"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
